import { LOCALSTORAGE_KEY } from "@/lib/constants";
import { request } from "@/lib/request";
import { parseWallpaperFileId } from "@/lib/wallpaperRefs";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../types";

const VERSION = 3;

export type ImageType = {
  address: string;
  fileId?: string;
  blurHash: string;
  data: {
    link: string;
    user: {
      name: string;
      username: string;
      link: string;
    };
    locationName: string;
    city: string;
    country: string;
  };
};

export type WallpaperConfigType =
  | { provider: "unsplash"; type: "random" }
  | { provider: "unsplash"; type: "topic"; topic: string }
  | { provider: "unsplash"; type: "images"; images: string[] }
  | { provider: "custom"; type: "custom"; images: string[] };

export interface WallpaperState {
  wallpapers: Record<
    string,
    {
      images: ImageType[];
      activeIndex: number;
      config: WallpaperConfigType;
    }
  >;
}

const initialState: WallpaperState = {
  wallpapers: {},
};

const generateKey = (
  key: "random" | "topic" | "images",
  {
    topicId,
    images,
  }: { topicId?: string; images?: { address: string; blurHash: string }[] },
) => {
  if (key === "random") {
    return "random";
  } else if (key === "topic") {
    return `topic_${topicId}`;
  }

  return `images_${images!.map(i => i.address).join("_")}`;
};

const getUnsplashCache = (
  key: "random" | "topic" | "images",
  {
    topicId,
    images,
  }: {
    topicId?: string;
    images?: { address: string; blurHash: string }[];
  } = {},
) => {
  const stored = JSON.parse(
    localStorage.getItem(`${LOCALSTORAGE_KEY}_IMAGES_${key}`) || "{}",
  );

  if (
    stored.key === generateKey(key, { images, topicId }) &&
    stored.images &&
    stored.images.length &&
    stored.lastUpdated > Date.now() - 1000 * 60 * 60 &&
    stored.version === VERSION
  ) {
    return stored;
  }

  return null;
};

const setUnsplashCache = (
  key: "random" | "topic" | "images",
  {
    images,
    topicId,
  }: { images: { address: string; blurHash: string }[]; topicId?: string },
) => {
  localStorage.setItem(
    `${LOCALSTORAGE_KEY}_IMAGES_${key}`,
    JSON.stringify({
      key: generateKey(key, { images, topicId }),
      images,
      lastUpdated: Date.now(),
      version: VERSION,
    }),
  );
};

const mapUnsplashImage = (image: any): ImageType => ({
  address: image.urls.raw,
  blurHash: image.blur_hash,
  data: {
    link: image.links.html,
    user: {
      name: image.user.name,
      username: image.user.username,
      link: image.user.links.html,
    },
    locationName: image.location?.name,
    city: image.location?.city,
    country: image.location?.country,
  },
});

export const fetchUnsplashRandomImages = async (): Promise<ImageType[]> => {
  const cache = getUnsplashCache("random");
  if (cache) {
    return cache.images;
  }

  const images = (await request.get(`/external/unsplash/random`)).data.data.map(
    (image: any) => mapUnsplashImage(image),
  );

  setUnsplashCache("random", { images });
  return images;
};

export const fetchUnsplashSelectedImages = async ({
  ids,
}: {
  ids: string[];
}): Promise<ImageType[]> => {
  const cache = getUnsplashCache("images", {
    images: ids.map(id => ({ address: id, blurHash: "" })),
  });
  if (cache) {
    return cache.images;
  }

  const images = (
    await Promise.all(
      ids.map(imageId => request.get(`/external/unsplash/photos/${imageId}`)),
    )
  ).map(({ data: { data: image } }: any) => mapUnsplashImage(image));

  setUnsplashCache("images", { images: images });
  return images;
};

export const fetchUnsplashTopicImages = async ({
  topicId,
}: {
  topicId: string;
}): Promise<ImageType[]> => {
  const cache = getUnsplashCache("topic", { topicId });
  if (cache) {
    return cache.images;
  }

  const images = (
    await request.get(`/external/unsplash/topics/${topicId}/photos?page=1`)
  ).data.data
    .map((image: any) => mapUnsplashImage(image))
    .flat();

  setUnsplashCache("topic", { images, topicId });
  return images;
};

export const fetchUnsplashImages = async (config: WallpaperConfigType) => {
  let images: ImageType[] = [];
  if (config.type === "random") {
    images = await fetchUnsplashRandomImages();
  } else if (config.type === "topic") {
    images = await fetchUnsplashTopicImages({ topicId: config.topic });
  } else if (config.type === "images") {
    images = await fetchUnsplashSelectedImages({ ids: config.images });
  }

  return images;
};

export const setWallpaperCache = async ({
  id,
  activeIndex,
  config,
  images,
}: {
  id: string;
  activeIndex: number;
  config: WallpaperConfigType;
  images: ImageType[];
}) => {
  const stored = JSON.parse(
    localStorage.getItem(`${LOCALSTORAGE_KEY}_WALLPAPER_STORE`) || "{}",
  );

  localStorage.setItem(
    `${LOCALSTORAGE_KEY}_WALLPAPER_STORE`,
    JSON.stringify({
      ...stored,
      [id]: {
        activeIndex,
        config,
        images,
      },
    }),
  );
};

export const initializeWallpaperStore = createAsyncThunk(
  "wallpaper/initializeStore",
  async () => {
    const stored = JSON.parse(
      localStorage.getItem(`${LOCALSTORAGE_KEY}_WALLPAPER_STORE`) || "{}",
    );

    return stored;
  },
);

export const initializeWallpaper = createAsyncThunk(
  "wallpaper/initialize",
  async (
    {
      id,
      config,
    }: {
      id: string;
      config: WallpaperConfigType;
    },
    { getState },
  ) => {
    const originalId = id;
    const state = (getState() as RootState).wallpaper;
    if (config.provider === "unsplash" && config.type === "random") {
      const stored = JSON.parse(
        localStorage.getItem(`${LOCALSTORAGE_KEY}_WALLPAPER_STORE`) || "{}",
      );

      localStorage.setItem(
        `${LOCALSTORAGE_KEY}_WALLPAPER_STORE`,
        JSON.stringify({
          ...stored,
          [id]: undefined,
        }),
      );

      id = "random";
    }

    const activeIndex =
      JSON.stringify(state.wallpapers[id]?.config) === JSON.stringify(config)
        ? state.wallpapers[id]?.activeIndex
        : 0;

    if (config.provider === "custom") {
      return {
        id,
        config,
        activeIndex,
        images: config.images.map(image => {
          const fileId = parseWallpaperFileId(image);

          return {
            address: fileId ? "" : image,
            ...(fileId ? { fileId } : {}),
            blurHash: "",
            data: {
              link: "",
              user: {
                name: "",
                username: "",
                link: "",
              },
              locationName: "",
              city: "",
              country: "",
            },
          };
        }),
      };
    }

    const images = await fetchUnsplashImages(config);
    return {
      id,
      originalId,
      config,
      activeIndex,
      images,
    };
  },
);

export const nextImage = createAsyncThunk(
  "wallpaper/nextImage",
  async ({ id }: { id: string }, { getState }) => {
    const configForOrigId = (getState() as RootState).wallpaper.wallpapers[id]
      ?.config;

    if (
      !configForOrigId ||
      (configForOrigId?.provider === "unsplash" &&
        configForOrigId?.type === "random")
    ) {
      id = "random";
    }

    const state = (getState() as RootState).wallpaper.wallpapers[id] || {
      activeIndex: 0,
      config: {
        provider: "unsplash",
        type: "random",
      },
      images: [],
    };
    const images =
      state.config.provider === "custom"
        ? state.images
        : await fetchUnsplashImages(state.config);
    const activeIndex =
      state.activeIndex + 1 < images.length ? state.activeIndex + 1 : 0;

    return {
      id,
      config: state.config,
      activeIndex,
      images,
    };
  },
);

export const previousImage = createAsyncThunk(
  "wallpaper/previousImage",
  async ({ id }: { id: string }, { getState }) => {
    const configForOrigId = (getState() as RootState).wallpaper.wallpapers[id]
      ?.config;
    if (
      configForOrigId?.provider === "unsplash" &&
      configForOrigId?.type === "random"
    ) {
      id = "random";
    }

    const state = (getState() as RootState).wallpaper.wallpapers[id] || {
      activeIndex: 0,
      config: {
        provider: "unsplash",
        type: "random",
      },
      images: [],
    };
    const images =
      state.config.provider === "custom"
        ? state.images
        : await fetchUnsplashImages(state.config);
    const activeIndex =
      state.activeIndex - 1 >= 0 ? state.activeIndex - 1 : images.length - 1;

    return {
      id,
      config: state.config,
      activeIndex,
      images,
    };
  },
);

const wallpaperSlice = createSlice({
  name: "wallpaper",
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(initializeWallpaperStore.fulfilled, (state, action) => {
        const stored = action.payload;
        if (stored) {
          Object.keys(stored).forEach(key => {
            const { activeIndex, config, images } = stored[key];
            state.wallpapers[key] = {
              activeIndex,
              config,
              images,
            };
          });
        }
      })
      .addCase(initializeWallpaperStore.rejected, (_, action) => {
        console.error("Failed to initialize wallpaper store:", action.error);
      })
      .addCase(initializeWallpaper.fulfilled, (state, action) => {
        state.wallpapers[action.payload.id] = {
          images: action.payload.images,
          activeIndex: action.payload.activeIndex,
          config: action.payload.config,
        };
        if (
          action.payload.originalId &&
          action.payload.originalId !== action.payload.id
        ) {
          delete state.wallpapers[action.payload.originalId];
        }

        setWallpaperCache(action.payload);
      })
      .addCase(initializeWallpaper.rejected, (_, action) => {
        console.error("Failed to initialize wallpaper:", action.error);
      })
      .addCase(nextImage.fulfilled, (state, action) => {
        state.wallpapers[action.payload.id] = {
          images: action.payload.images,
          activeIndex: action.payload.activeIndex,
          config: action.payload.config,
        };

        setWallpaperCache(action.payload);
      })
      .addCase(nextImage.rejected, (_, action) => {
        console.error("Failed to get next image:", action.error);
      })
      .addCase(previousImage.fulfilled, (state, action) => {
        state.wallpapers[action.payload.id] = {
          images: action.payload.images,
          activeIndex: action.payload.activeIndex,
          config: action.payload.config,
        };

        setWallpaperCache(action.payload);
      })
      .addCase(previousImage.rejected, (_, action) => {
        console.error("Failed to get previous image:", action.error);
      });
  },
});

export default wallpaperSlice.reducer;
